import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDataCache } from '../contexts/DataCacheContext';
import { configService } from '../services/configService';
import FeatureEngagementModal from './FeatureEngagementModal';
import CountryAvailabilityModal from './CountryAvailabilityModal';
import { useCountryAvailability } from '../hooks/useCountryAvailability';
import Header from './Header';
import { Button } from './figma/FigmaComponents';

function Plans({ skipRedirect = false }) {
  const navigate = useNavigate();
  const { user, updateUserProfile, logout } = useAuth();
  const { cache, prefetchPlans } = useDataCache();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTocModal, setShowTocModal] = useState(false);
  const [tocAccepted, setTocAccepted] = useState(false);

  const [engagementModal, setEngagementModal] = useState({ isOpen: false, plan: null });
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [redirectCount, setRedirectCount] = useState(() => {
    // Reset count on page refresh
    return sessionStorage.getItem('plansRedirectCount') ? parseInt(sessionStorage.getItem('plansRedirectCount')) : 0;
  });
  const { showCountryModal, countryCode, userEmail, closeModal } = useCountryAvailability();
  const [togglingAutotopup, setTogglingAutotopup] = useState(false);
  const [customerSessionSecret, setCustomerSessionSecret] = useState(null);

  useEffect(() => {
    if (!cache.plans) {
      prefetchPlans();
    }
  }, []);

  useEffect(() => {
    // Create Stripe customer session for pricing table
    const createSession = async () => {
      if (user?.attributes?.sub) {
        try {
          // Assign reseller if referral code exists in localStorage
          const referralCode = localStorage.getItem('referralCode');
          if (referralCode) {
            try {
              await configService.assignReseller(user.attributes.sub, referralCode);
              console.log('✅ Reseller assigned:', referralCode);
              localStorage.removeItem('referralCode');
            } catch (error) {
              console.warn('⚠️ Failed to assign reseller:', error);
            }
          }
          
          const session = await configService.createCustomerSession();
          setCustomerSessionSecret(session.client_secret);
        } catch (error) {
          console.error('Failed to create customer session:', error);
          // Pricing table will still work, just without pre-filled metadata
          // Webhook will need to rely on email lookup as fallback
        }
      }
    };
    createSession();
  }, [user]);

  const hasChecked = React.useRef(false);
  useEffect(() => {
    if (hasChecked.current) return;
    hasChecked.current = true;
    checkSubscriptionAndRedirect();
  }, [user]);

  useEffect(() => {
    // Load Stripe pricing table script
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/pricing-table.js';
    script.async = true;
    document.head.appendChild(script);
    
    
    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://js.stripe.com/v3/pricing-table.js"]');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, [user]);

  const checkSubscriptionAndRedirect = async () => {

    if (!user) {
      setCheckingSubscription(false);
      return;
    }
    
    // Check email verification status (skip for OAuth users)
    const isOAuthUser = user?.username?.includes('Google_') || user?.username?.includes('Facebook_') || user?.username?.includes('LoginWithAmazon_') || user?.username?.includes('SignInWithApple_');
    const emailVerified = user?.attributes?.email_verified;
    
    // Redirect loop protection - stop redirecting after 3 attempts
    if (!isOAuthUser && (emailVerified === false || emailVerified === 'false')) {
      const newCount = redirectCount + 1;
      setRedirectCount(newCount);
      sessionStorage.setItem('plansRedirectCount', newCount.toString());
      
      if (newCount < 3) {
        navigate('/login');
        return;
      } else {
        navigate('/login?verification_issue=true');
        return;
      }
    } else if (isOAuthUser) {
      // OAuth user detected, skipping email verification check
    }

    try {
      const status = await configService.getSubscriptionStatus();
      
      // Fetch usage data for prepaid plans to get remaining minutes
      if (status?.payment_type === 'prepaid') {
        try {
          const usageData = await configService.getUsageData();
          status.prepaid_balance_minutes = usageData.remaining_minutes;
        } catch (usageError) {
          console.warn('Failed to fetch usage data:', usageError);
          status.prepaid_balance_minutes = 0;
        }
      }
      
      setSubscriptionStatus(status);
      
      // 🚀 UPDATE JWT with subscription status only if it differs
      const currentJwtStatus = user?.attributes?.['custom:subscription_status'];
      if (currentJwtStatus !== (status?.status || 'inactive')) {
        try {
          await updateUserProfile({ 
            'custom:subscription_status': status?.status || 'inactive'
          });
        } catch (jwtError) {
          console.warn('💳 PLANS: Failed to update JWT, but continuing:', jwtError);
        }
      }
      
      // If user has active subscription or prepaid, check setup completion via API
      const hasActivePayment = status?.status === 'active' || status?.status === 'trialing' || status?.payment_type === 'prepaid';
      
      if (hasActivePayment && !skipRedirect) {
        try {
          // Check setup completion via API to be sure
          const cognitoUserId = user?.attributes?.sub;
          const config = await configService.getBusinessConfigByUserId(cognitoUserId);
          
          const setupComplete = !!(config && 
            config.business_name && 
            config.phone_number && 
            config.website && 
            config.address &&
            config.business_description && 
            config.assistant_name &&
            config.voice_settings && 
            config.formality !== undefined && 
            config.verbosity !== undefined && 
            config.empathy !== undefined &&
            config.assistant_phone &&
            config.manager_number && 
            config.escalation_email);
          
          
          if (setupComplete) {
            navigate('/dashboard');
          } else {
            navigate('/setup');
          }
        } catch (configError) {
          navigate('/setup');
        }
        return;
      }
      
      // No valid subscription, staying on plans page
    } catch (error) {
      // Could not check subscription status - update JWT with inactive status
      try {
        await updateUserProfile({ 'custom:subscription_status': 'inactive' });
      } catch (jwtError) {
        console.warn('💳 PLANS: Failed to update JWT with inactive status:', jwtError);
      }
    } finally {
      setCheckingSubscription(false);
    }

    // Check TOC acceptance
    const accepted = localStorage.getItem('tocAccepted');
    if (accepted === 'true') {
      setTocAccepted(true);
    } else {
      setShowTocModal(true);
    }
  };



  // Show loading while checking subscription
  if (checkingSubscription) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-text-secondary">Checking subscription status...</div>
      </div>
    );
  }



  const handleTocAgree = () => {
    localStorage.setItem('tocAccepted', 'true');
    setTocAccepted(true);
    setShowTocModal(false);
  };

  const handleTocCancel = () => {
    logout();
  };

  const selectPlan = async (plan) => {
    if (!tocAccepted) {
      setShowTocModal(true);
      return;
    }

    // Check if this is a waitlist product
    if (plan.metadata?.WaitList === 'True') {
      setEngagementModal({
        isOpen: true,
        plan: plan
      });
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      // 1. Save plan selection immediately (before Stripe checkout)
      await configService.selectSubscriptionPlan(plan.price_id);
      // 2. Create Stripe checkout session
      const checkoutData = {
        price_id: plan.price_id,
        success_url: `${window.location.origin}/setup?payment=success&plan=${plan.price_id}`,
        cancel_url: `${window.location.origin}/plans?payment=cancelled`,
        metadata: {
          cognito_user_id: user?.attributes?.sub  // Use Cognito user ID from JWT
        }
        // coupon_id: 'COUPON_ID_HERE' // Uncomment to pre-apply a specific coupon
      };
      
      const response = await configService.createCheckoutSession(checkoutData);
      window.location.href = response.checkout_url;
    } catch (error) {
      if (error.message.includes('business_id not found')) {
        setError('Please complete your business setup first. Redirecting to setup...');
        setTimeout(() => navigate('/setup'), 2000);
      } else {
        setError('Failed to select plan or start checkout. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const closeEngagementModal = () => {
    setEngagementModal({ isOpen: false, plan: null });
  };

  const toggleAutotopup = async () => {
    setTogglingAutotopup(true);
    try {
      const newStatus = !subscriptionStatus.autotopup_enabled;
      await configService.updateAutotopup(newStatus);
      setSubscriptionStatus(prev => ({ ...prev, autotopup_enabled: newStatus }));
    } catch (error) {
      console.error('Failed to toggle auto-topup:', error);
      setError('Failed to update auto-topup setting');
    } finally {
      setTogglingAutotopup(false);
    }
  };

  return (
    <>
      {showTocModal && !subscriptionStatus?.status && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-sm max-w-md w-full mx-4 shadow-card">
            <h2 className="text-h2 text-text-primary mb-4">Terms of Conditions Agreement</h2>
            <p className="text-body text-text-secondary mb-4">Before selecting a plan, you must agree to our Terms of Conditions.</p>
            <p className="text-body text-text-secondary mb-6">By clicking "Agree", you acknowledge that you have read and agree to be bound by our <Link to="/toc" target="_blank" className="text-primary hover:underline">Terms of Conditions</Link>.</p>
            <div className="flex gap-4">
              <Button variant="primary" onClick={handleTocAgree} fullWidth>
                Agree
              </Button>
              <Button variant="outline" onClick={handleTocCancel} fullWidth>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <div className="min-h-screen bg-gray-50">
        <div className="pt-8 pb-16 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-h1 text-text-primary mb-4">
                Subscription Plans
              </h1>
            </div>

            {error && (
              <div className="bg-red-100 text-red-600 p-4 rounded-sm mb-8 text-center max-w-2xl mx-auto">
                {error}
              </div>
            )}

            {subscriptionStatus?.status === 'canceled' && (
              <div className="bg-orange-100 border-2 border-orange-300 text-orange-900 p-6 rounded-sm mb-8 text-center max-w-2xl mx-auto">
                <div className="text-h2 mb-2">⚠️ Subscription Canceled</div>
                <div className="text-body mb-2">
                  You have <strong>{subscriptionStatus.days_remaining} days</strong> remaining to reactivate before your data is deleted.
                </div>
                <Link to="/canceled" className="text-primary hover:underline font-semibold">
                  View details and export call history →
                </Link>
              </div>
            )}

            {subscriptionStatus?.status === 'active' && (
              <div className="bg-white border border-border-line rounded-sm p-6 mb-8 max-w-4xl mx-auto shadow-card">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mr-4">
                    <span className="text-white text-h2">✓</span>
                  </div>
                  <div>
                    <h3 className="text-h2 text-text-primary">Active Subscription</h3>
                    <p className="text-body text-text-secondary">Your plan is currently active and running</p>
                  </div>
                </div>
                <div className="bg-bg-section rounded-sm p-4 mb-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-body">
                    <div>
                      <span className="font-semibold text-text-secondary">Plan:</span>
                      <div className="text-h3 text-text-primary">{subscriptionStatus.plan_id || 'Unknown Plan'}</div>
                    </div>
                    <div>
                      <span className="font-semibold text-text-secondary">Status:</span>
                      <div className="text-h3 text-primary capitalize">{subscriptionStatus.status}</div>
                    </div>
                    <div>
                      <span className="font-semibold text-text-secondary">
                        {subscriptionStatus.payment_type === 'prepaid' ? 'Prepaid Balance:' : 'Cost:'}
                      </span>
                      <div className="text-h3 text-text-primary">
                        {subscriptionStatus.payment_type === 'prepaid' 
                          ? `${subscriptionStatus.prepaid_balance_minutes || 0} minutes`
                          : `$${subscriptionStatus.base_price ? (subscriptionStatus.base_price / 100).toFixed(2) : 'N/A'}`
                        }
                      </div>
                    </div>
                  </div>
                  {subscriptionStatus.payment_type === 'prepaid' && (
                    <div className="mt-4 pt-4 border-t border-border-line">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-semibold text-text-secondary">Auto-topup:</span>
                          <div className="text-sm text-text-secondary mt-1">
                            {subscriptionStatus.autotopup_enabled 
                              ? 'Automatically top up when balance is low' 
                              : 'You will receive notifications to top up manually'}
                          </div>
                        </div>
                        <button
                          onClick={toggleAutotopup}
                          disabled={togglingAutotopup}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                            subscriptionStatus.autotopup_enabled ? 'bg-primary' : 'bg-gray-200'
                          } ${togglingAutotopup ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              subscriptionStatus.autotopup_enabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  )}
                  {subscriptionStatus.payment_type !== 'prepaid' && subscriptionStatus.current_period_end && (
                    <div className="mt-4 pt-4 border-t border-border-line">
                      <span className="font-semibold text-text-secondary">Next billing date:</span>
                      <div className="text-text-primary">
                        {new Date(subscriptionStatus.current_period_end * 1000).toLocaleDateString()}
                      </div>
                    </div>
                  )}
                </div>
                <div className="text-center">
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link to="/dashboard">
                      <Button variant="primary">Go to Dashboard</Button>
                    </Link>
                    {subscriptionStatus.payment_type === 'prepaid' && (
                      <Button 
                        variant="primary"
                        onClick={async () => {
                          try {
                            const cognitoUserId = user?.attributes?.sub;
                            const prefillUrl = `https://buy.stripe.com/aFadR94VYc1fd9i6Mn3F601?client_reference_id=${cognitoUserId}&prefilled_email=${encodeURIComponent(user?.attributes?.email || '')}`;
                            window.location.href = prefillUrl;
                          } catch (error) {
                            console.error('Failed to create top-up session:', error);
                            setError('Failed to open top-up page. Please try again.');
                          }
                        }}
                      >
                        Top Up Balance
                      </Button>
                    )}
                    <Button 
                      variant="primary"
                      onClick={async () => {
                        try {
                          const session = await configService.createBillingPortalSession();
                          window.open(session.url, '_blank');
                        } catch (error) {
                          console.error('Failed to create billing portal session:', error);
                          setError('Failed to open billing portal. Please try again.');
                        }
                      }}
                    >
                      Manage Subscription
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div 
              id="pricing-table" 
              className="max-w-6xl mx-auto" 
              style={{ display: subscriptionStatus?.status === 'active' ? 'none' : 'block' }}
            >
              <stripe-pricing-table 
                pricing-table-id={process.env.REACT_APP_STRIPE_PRICING_TABLE_ID || "prctbl_1SXy7kGuQEhKL8AnuVtakHZ5"}
                publishable-key={process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "pk_live_51S3o7CGuQEhKL8An8Pf0jZPzF3vjXyPkhhWNP9sqwOb4fy3edtb6FLdmOLTh9rBUMlCeAZcOVcL2Ts0bglMHSOja00KTJGOqhY"}
                customer-email={user?.attributes?.email}
                customer-session-client-secret={customerSessionSecret}
              >
              </stripe-pricing-table>
            </div>
          </div>
        </div>
      </div>
      
      <FeatureEngagementModal
        isOpen={engagementModal.isOpen}
        onClose={closeEngagementModal}
        featureType="subscription_plan"
        featureId={engagementModal.plan?.id}
        featureName={engagementModal.plan?.name}
        context={{
          page: 'plans',
          location: 'pricing_card',
          price_id: engagementModal.plan?.price_id
        }}
      />
      
      <CountryAvailabilityModal 
        isOpen={showCountryModal}
        onClose={closeModal}
        countryCode={countryCode}
        email={userEmail}
      />      
    </>
  );
}

export default Plans;